import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';

import { LifecycleStage } from '../lifecycle/_types';
import { denormalize, normalize } from '../shared/normalizers';

import { LifecycleTransition } from './_types';

interface LifecycleQueryParams {
  page?: number;
  per?: number;
  userId: Identifier;
  sort?: string;
  search?: string;
}

interface FetchLifecycleTransitionsResponse {
  lifecycleTransitions: LifecycleTransition[];
  total: number;
}

export const fetchAllLifecycleStages = async (): Promise<LifecycleStage[]> => {
  const response = await api.get('/api/v4/lifecycle_stages', {
    params: {
      fetch_all: true,
      'q[s]': 'sort_order asc',
    },
  });

  return response.data.map((stage) => camelcaseKeys(stage));
};

interface FetchLifecycleOptions {
  page?: number;
  perPage?: number;
  search?: string;
}

export const fetchLifecycleQueryKey = (o: FetchLifecycleOptions) => [
  'lifecycle',
  o.page,
  o.perPage,
  o.search,
];

export const fetchLifecycleStages = async ({
  page = 0,
  perPage = 20,
  search = '',
}: FetchLifecycleOptions): Promise<{
  total: number;
  data: LifecycleStage[];
}> => {
  try {
    const response = await api.get('/api/v4/lifecycle_stages', {
      params: {
        fetch_all: true,
        page,
        per: perPage,
        'q[name_or_description_cont]': search,
        'q[s]': 'sort_order asc',
      },
    });

    return {
      data: response.data.lifecycle_stage.map((stage) => normalize(stage)),
      total: response.data.meta.total,
    };
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error fetching lifecycle stages.'));
  }
};

export const fetchLifecycleStage = async (
  id: Identifier
): Promise<LifecycleStage> => {
  try {
    const response = await api.get(`/api/v4/lifecycle_stages/${id}`);
    return normalize(response.data);
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error fetching lifecycle stage.'));
  }
};

export const fetchLifecycleTransitions = async ({
  page = 1,
  per = 25,
  userId,
  sort,
  search,
}: LifecycleQueryParams): Promise<FetchLifecycleTransitionsResponse> => {
  const urlParams = {
    page,
    per,
    user_id: userId,
    'q[s]': sort || 'date desc',
  };

  if (search) {
    urlParams['q[from_lifecycle_stage_name_or_to_lifecycle_stage_name_cont]'] =
      search;
  }

  const response = await api.get('/api/v4/lifecycle_transitions', {
    params: urlParams,
  });

  return {
    lifecycleTransitions: response.data.lifecycle_transition.map((transition) =>
      camelcaseKeys(transition)
    ),
    total: response.data.meta.total,
  };
};

export const deleteLifecycleStage = async (lifecycleStageId: Identifier) => {
  try {
    const response = await api.delete(
      `/api/v4/lifecycle_stages/${lifecycleStageId}`
    );

    return normalize(response.data);
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error deleting lifecycle stage.'));
  }
};

export const updateLifecycleStage = async (
  lifecycleStage: LifecycleStage
): Promise<LifecycleStage> => {
  try {
    const response = await api.put(
      `/api/v4/lifecycle_stages/${lifecycleStage.id}`,
      { lifecycle_stage: denormalize(lifecycleStage) }
    );

    return normalize(response.data);
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error updating lifecycle stage.'));
  }
};

export const addLifecycleStage = async (
  lifecycleStage: LifecycleStage
): Promise<LifecycleStage> => {
  try {
    const response = await api.post(`/api/v4/lifecycle_stages`, {
      lifecycle_stage: denormalize(lifecycleStage),
    });

    return normalize(response.data);
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error adding lifecycle stage.'));
  }
};
